import {
  WrenchScrewdriverIcon,
  CheckIcon,
  XCircleIcon,
  PencilIcon,
  InformationCircleIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";

const machineToolOptions = [
  "Long Screw Small",
  "Long Screw Medium",
  "Long Screw Large",
  "Short Small",
  "Short Large",
  "None",
  "Long-for-Bagging",
];

const RoomMachineToolConfig = ({
  roomMachineTool,
  isEditMode,
  machineToolError,
  onToggleEditMode,
  onSaveConfig,
  onMachineToolUpdate,
  canEdit = true,
}) => {
  const toMachineToolKey = (label) =>
    label
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

  const getBadgeColor = (tool) => {
    const colors = {
      "Long Screw Small": "bg-green-100 text-green-800",
      "Long Screw Medium": "bg-yellow-100 text-yellow-800",
      "Long Screw Large": "bg-red-100 text-red-800",
      "Short Small": "bg-blue-100 text-blue-800",
      "Short Large": "bg-purple-100 text-purple-800",
      "None": "bg-gray-200 text-gray-700",
      "Long-for-Bagging": "bg-teal-100 text-teal-800",
    };
    return colors[tool] || "bg-gray-100 text-gray-800";
  };

  const getDescription = (tool) => {
    if (tool === "None") return "No machine tool / Manual";
    return tool;
  };

  const getTimeColor = (minutes, tool) => {
    if (tool === "None") return "text-gray-700 font-semibold";
    if (minutes >= 20) return "text-red-600";
    if (minutes >= 15) return "text-yellow-600";
    if (minutes >= 10) return "text-green-600";
    return "text-gray-600";
  };

  const handleEnterEditMode = () => {
    if (canEdit) {
      onToggleEditMode();
    }
  };

  return (
    <div className="mb-8 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div className="flex items-start gap-3">
          <WrenchScrewdriverIcon className="w-5 h-5 text-indigo-500 mt-1.5 flex-shrink-0" />
          <div>
            <h4 className="text-lg font-semibold text-gray-900">
              Room Machine Tool Configuration
            </h4>
            <p className="text-sm text-gray-500 mt-1">
              Configure machine tool setup times (minutes) for each room.{" "}
              {machineToolError && "(Using default times)"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isEditMode ? (
            <>
              <button
                onClick={onSaveConfig}
                disabled={!canEdit}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-sm flex items-center gap-2 ${
                  canEdit
                    ? "bg-black text-white hover:bg-gray-900"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                <CheckIcon className="w-4 h-4" />
                Save Changes
              </button>
              <button
                onClick={onToggleEditMode}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-all duration-200 flex items-center gap-2"
              >
                <XCircleIcon className="w-4 h-4" />
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={handleEnterEditMode}
              disabled={!canEdit}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-sm flex items-center gap-2 ${
                canEdit
                  ? "bg-black text-white hover:bg-gray-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              {canEdit ? (
                <PencilIcon className="w-4 h-4" />
              ) : (
                <LockClosedIcon className="w-4 h-4" />
              )}
              {canEdit ? "Edit Setup Times" : "Edit Locked"}
            </button>
          )}
        </div>
      </div>

      {/* Info notice */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <InformationCircleIcon className="w-4 h-4 flex-shrink-0" />
          <span>
            "None" represents no machine tool usage (always 0 minutes –
            non-editable)
          </span>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden sm:block overflow-x-auto">
        <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200 min-w-max">
          <table className="w-full border-collapse min-w-max">
            <thead>
              <tr className="bg-gradient-to-r from-blue-50 to-indigo-50">
                <th className="py-4 px-6 text-left font-semibold text-gray-900 border-b border-gray-200 sticky left-0 bg-gradient-to-r from-blue-50 to-indigo-50 z-10 min-w-[180px]">
                  <div className="flex items-center gap-2">
                    <WrenchScrewdriverIcon className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                    <span>Machine Tool</span>
                  </div>
                </th>

                {roomMachineTool.map((roomConfig) => (
                  <th
                    key={roomConfig._id || roomConfig.room}
                    className="py-4 px-4 text-center font-semibold text-gray-900 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 min-w-[140px]"
                  >
                    {roomConfig.room}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {machineToolOptions.map((tool, toolIndex) => {
                const isNone = tool === "None";
                const isBaggingOnly = tool === "Long-for-Bagging";

                return (
                  <tr
                    key={tool}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      toolIndex % 2 === 0 ? "bg-white" : "bg-gray-50"
                    } ${isNone ? "bg-gray-100 font-medium" : ""}`}
                  >
                    {/* Sticky first column */}
                    <td className="py-4 px-4 font-semibold text-gray-900 border-b border-gray-200 sticky left-0 bg-gray-50 z-10">
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-medium ${getBadgeColor(tool)}`}
                        >
                          {tool}
                        </span>
                        <span className="text-sm truncate">
                          {getDescription(tool)}
                        </span>
                      </div>
                    </td>

                    {roomMachineTool.map((roomConfig, roomIndex) => {
                      const keyName = toMachineToolKey(tool);
                      const isRoom5Bags = roomConfig.room === "Room 5 Bags";
                      const isLocked = isNone || (isBaggingOnly && !isRoom5Bags);
                      const currentTime =
                        isBaggingOnly && !isRoom5Bags
                          ? 0
                          : roomConfig.machineTool[keyName] ?? 0;

                      return (
                        <td
                          key={`${roomConfig._id || roomConfig.room}-${tool}`}
                          className="py-4 px-4 border-b border-gray-200 text-center"
                        >
                          {isEditMode ? (
                            isLocked ? (
                              <div className="flex flex-col items-center gap-1 opacity-70">
                                <div className="w-20 px-3 py-2 text-center border border-gray-300 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 cursor-not-allowed">
                                  {currentTime}
                                </div>
                                <span className="text-xs text-gray-500">
                                  {isNone ? "minutes (fixed)" : "Room 5 only"}
                                </span>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center gap-1">
                                <input
                                  type="number"
                                  value={currentTime}
                                  onChange={(e) =>
                                    onMachineToolUpdate(
                                      roomIndex,
                                      tool,
                                      e.target.value,
                                    )
                                  }
                                  disabled={!canEdit}
                                  className={`w-20 px-3 py-2 text-center border-2 rounded-lg text-sm font-medium transition-all ${
                                    canEdit
                                      ? "border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      : "border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed"
                                  }`}
                                  min="0"
                                />
                                <span className="text-xs text-gray-500">
                                  minutes
                                </span>
                              </div>
                            )
                          ) : (
                            <div className="flex flex-col items-center gap-1">
                              <span
                                className={`text-lg font-bold ${getTimeColor(currentTime, tool)}`}
                              >
                                {currentTime}
                              </span>
                              <span className="text-xs text-gray-500">
                                minutes
                              </span>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards – similar logic */}
      <div className="sm:hidden space-y-4">
              {machineToolOptions.map((tool) => {
                const isNone = tool === "None";
                const isBaggingOnly = tool === "Long-for-Bagging";

                return (
            <div
              key={tool}
              className={`p-4 rounded-xl border shadow-sm ${
                isNone
                  ? "bg-gray-50 border-gray-300"
                  : "bg-white border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-medium ${getBadgeColor(tool)}`}
                  >
                    {tool}
                  </span>
                  <span className="text-sm font-medium">
                    {getDescription(tool)}
                  </span>
                </div>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  minutes
                </span>
              </div>

              <div className="space-y-3">
                {roomMachineTool.map((roomConfig, roomIndex) => {
                  const keyName = toMachineToolKey(tool);
                  const isRoom5Bags = roomConfig.room === "Room 5 Bags";
                  const isLocked = isNone || (isBaggingOnly && !isRoom5Bags);
                  const currentTime =
                    isBaggingOnly && !isRoom5Bags
                      ? 0
                      : roomConfig.machineTool[keyName] ?? 0;

                  return (
                    <div
                      key={roomConfig._id || roomConfig.room}
                      className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-100"
                    >
                      <span className="text-sm font-medium text-gray-900">
                        {roomConfig.room}
                      </span>

                      {isEditMode ? (
                        isLocked ? (
                          <div className="text-right">
                            <div className="px-4 py-2 bg-gray-100 border border-gray-300 rounded text-sm font-medium text-gray-700 cursor-not-allowed">
                              {currentTime}
                            </div>
                            <span className="text-xs text-gray-500 mt-1 block">
                              {isNone ? "fixed" : "Room 5 only"}
                            </span>
                          </div>
                        ) : (
                          <div className="text-right">
                            <input
                              type="number"
                              value={currentTime}
                              onChange={(e) =>
                                onMachineToolUpdate(
                                  roomIndex,
                                  tool,
                                  e.target.value,
                                )
                              }
                              disabled={!canEdit}
                              className={`w-20 px-3 py-2 text-center border rounded text-sm ${
                                canEdit
                                  ? "border-gray-300 focus:border-blue-500"
                                  : "bg-gray-100 text-gray-500 cursor-not-allowed"
                              }`}
                              min="0"
                            />
                            <span className="text-xs text-gray-500 mt-1 block">
                              min
                            </span>
                          </div>
                        )
                      ) : (
                        <div className="text-right">
                          <span
                            className={`text-base font-bold ${getTimeColor(currentTime, tool)}`}
                          >
                            {currentTime}
                          </span>
                          <span className="text-xs text-gray-500 block">
                            min
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit Mode Notice */}
      {isEditMode && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="flex items-start gap-3 text-sm text-blue-800">
            <InformationCircleIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <p>
                <strong>"None"</strong> is fixed at 0 minutes for all rooms and
                cannot be changed.
              </p>
              <p className="mt-1 text-blue-700 text-xs">
                Edit setup times for other machine tools as needed.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomMachineToolConfig;
